import { logger } from "@/utils/logger";
import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useInteractivePaymentSelector } from '@/hooks/useInteractivePaymentSelector';
import { usePaymentPreferences } from '@/contexts/PaymentPreferencesContext';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

interface PaymentSelectorProps {
  styleId?: string | number;
  vin?: string;
  initialPaymentType?: 'Finance' | 'Lease';
  initialTerm?: number;
  initialKmAllowance?: number;
  frequency?: string;
}

const PaymentSelector: React.FC<PaymentSelectorProps> = ({
  styleId,
  vin,
  initialPaymentType,
  initialTerm,
  initialKmAllowance,
  frequency = 'monthly'
}) => {
  const { language } = useLanguage();
  const { preferences, updatePreferences } = usePaymentPreferences();
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Determine the identifier to use (styleId or vin)
  const identifier = styleId || vin;
  
  if (!identifier) {
    console.error('PaymentSelector: Either styleId or vin must be provided');
    return null;
  }
  
  // Use global preferences if available, otherwise use initial values
  const effectivePaymentType = preferences.paymentType || initialPaymentType;
  const effectiveTerm = preferences.term || initialTerm;
  const effectiveKmAllowance = preferences.kmAllowance || initialKmAllowance;
  
  const {
    selectedPayment,
    availableOptions,
    selectedTerm,
    selectedKmAllowance,
    paymentType,
    cycleTerm,
    cycleKmAllowance,
    changePaymentType,
    financeRate,
    leaseRate,
    loading,
    error
  } = useInteractivePaymentSelector(identifier, effectivePaymentType, effectiveTerm, effectiveKmAllowance, frequency, vin ? 'vin' : 'styleid');

  // Sync local changes to global preferences
  useEffect(() => {
    if (selectedTerm !== null || selectedKmAllowance !== null) {
      updatePreferences({
        paymentType,
        term: selectedTerm,
        kmAllowance: selectedKmAllowance
      });
    }
  }, [paymentType, selectedTerm, selectedKmAllowance]);

  // Hide banner if there's an error or no data after loading completes
  if (vin) {
    logger.log('💳 PaymentSelector: Render check (VIN)', { 
      identifier,
      paramType: 'vin',
      error, 
      loading, 
      selectedPayment: selectedPayment ? {
        amount: selectedPayment.amount,
        type: selectedPayment.type,
        term: selectedPayment.term,
        kmAllowance: selectedPayment.kmAllowance
      } : null,
      selectedTerm,
      selectedKmAllowance,
      paymentType,
      frequency,
      availableTerms: availableOptions.terms,
      availableKmAllowances: availableOptions.kmAllowances
    });
  }

  if (error) {
    if (vin) {
      logger.log('❌ PaymentSelector: Hiding due to ERROR (VIN):', error);
    }
    return null;
  }
  
  if (!loading && !selectedPayment) {
    if (vin) {
      logger.log('❌ PaymentSelector: Hiding - No payment data after loading (VIN)', {
        identifier,
        paramType: 'vin'
      });
    }
    return null;
  }

  if (loading) {
    return (
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-green-500 via-green-600 to-green-500 text-white px-3 py-2 rounded-t-lg shadow-xl z-10">
        <div className="flex items-center justify-center">
          <span className="text-xs">
            {language === 'fr' ? 'Chargement...' : 'Loading...'}
          </span>
        </div>
      </div>
    );
  }

  const formatFrequency = (freq: string) => {
    if (language === 'fr') {
      return freq === 'weekly' ? 'semaine' : freq === 'biweekly' ? '2 semaines' : 'mois';
    }
    return freq === 'weekly' ? 'week' : freq === 'biweekly' ? 'bi-weekly' : 'month';
  };

  const formatAmount = () => {
    const amount = Math.round(parseFloat(selectedPayment.amount.replace(/,/g, '')));
    const freq = formatFrequency(frequency);
    
    if (language === 'fr') {
      return {
        amount: '&Agrave; partir de ' + amount + ' $',
        frequency: '/ ' + freq
      };
    } else {
      return {
        amount: `Starting at $${amount}`,
        frequency: `/ ${freq}`
      };
    }
  };

  const formattedText = formatAmount();
  const showTermControls = availableOptions.terms.length > 1;
  const showKmControls = selectedPayment.type === 'Lease' && availableOptions.kmAllowances.length > 1;

  return (
    <div className="bg-gradient-to-r from-green-500 via-green-600 to-green-500 text-white px-3 py-3 rounded-t-lg shadow-xl">
      <div className="flex flex-col items-center justify-center gap-2">
        {/* Payment Amount with Expand/Collapse Toggle */}
        <div 
          className="flex items-center gap-1.5 cursor-pointer w-full justify-center hover:opacity-90 transition-opacity"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Sparkles className="w-3 h-3 animate-pulse flex-shrink-0" />
          <span className="text-sm tracking-tight drop-shadow-lg whitespace-nowrap">
            <span
              className="font-bold"
              dangerouslySetInnerHTML={{ __html: formattedText.amount }}
            />
            <span
              className="text-xs font-normal ml-1"
              dangerouslySetInnerHTML={{ __html: formattedText.frequency }}
            />
          </span>
          <Sparkles className="w-3 h-3 animate-pulse flex-shrink-0" />
          {isExpanded ? (
            <ChevronUp className="w-3.5 h-3.5 ml-1 flex-shrink-0" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 ml-1 flex-shrink-0" />
          )}
        </div>

        {/* Expandable Controls */}
        {isExpanded && (
          <div className="flex flex-col items-center gap-1 w-full animate-fade-in">
            {/* Payment Type Radio Buttons */}
            <div className="flex items-center justify-center gap-3 text-sm">
              <RadioGroup
                value={paymentType}
                onValueChange={(value) => changePaymentType(value as 'Finance' | 'Lease')}
                className="flex items-center gap-3"
              >
                <div className="flex items-center gap-1">
                  <RadioGroupItem value="Lease" id="lease" className="h-3 w-3 border-white text-white data-[state=checked]:bg-white data-[state=checked]:border-white" />
                  <Label htmlFor="lease" className="cursor-pointer font-medium text-white whitespace-nowrap !text-[0.8rem] !leading-[1.1rem]">
                    {language === 'fr' ? 'Loc.' : 'Lease'} {leaseRate}%
                  </Label>
                </div>
                <div className="flex items-center gap-1">
                  <RadioGroupItem value="Finance" id="finance" className="h-3 w-3 border-white text-white data-[state=checked]:bg-white data-[state=checked]:border-white" />
                  <Label htmlFor="finance" className="cursor-pointer font-medium text-white whitespace-nowrap !text-[0.8rem] !leading-[1.1rem]">
                    {language === 'fr' ? 'Fin.' : 'Fin.'} {financeRate}%
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Interactive Controls */}
            <div className="flex items-center gap-3 text-[10px]">
              {/* Term Selector */}
              {showTermControls && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => cycleTerm('prev')}
                    className="hover:bg-white/20 rounded p-0.5 transition-colors"
                    aria-label="Previous term"
                  >
                    <ChevronLeft className="w-3 h-3" />
                  </button>
                  <span className="font-medium min-w-[60px] text-center">
                    {selectedTerm} {language === 'fr' ? 'mois' : 'mth'}
                  </span>
                  <button
                    onClick={() => cycleTerm('next')}
                    className="hover:bg-white/20 rounded p-0.5 transition-colors"
                    aria-label="Next term"
                  >
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* KM Allowance Selector */}
              {showKmControls && (
                <>
                  <span className="opacity-60">|</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => cycleKmAllowance('prev')}
                      className="hover:bg-white/20 rounded p-0.5 transition-colors"
                      aria-label="Previous km allowance"
                    >
                      <ChevronLeft className="w-3 h-3" />
                    </button>
                    <span className="font-medium min-w-[70px] text-center">
                      {selectedKmAllowance?.toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA')} km
                    </span>
                    <button
                      onClick={() => cycleKmAllowance('next')}
                      className="hover:bg-white/20 rounded p-0.5 transition-colors"
                      aria-label="Next km allowance"
                    >
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSelector;
